import React, {
    useCallback,
    memo,
    useMemo,
    useEffect,
    useReducer,
} from 'react';
import { useMediaQuery } from 'react-responsive';
import PropTypes from 'prop-types';

import Traductor from 'components/Traductor';
import CountDown from 'components/CountDown';
import { isAppleDeviceOrNavegatorForDates } from 'Functions';
import {
    checkAuctionIsActive,
    formatNumber,
} from 'js/components/shared/vehicleFunctions';

const Summary = ({
    data,
    directBuyAmount,
    miniumBidAmount,
    loading,
    amountsForm,
    onBid,
    onDirectBuy,
    disabled,
    onCountDownEnd,
    serverTime,
}) => {
    const [{ loadingBidButton, loadingBuyButton }, setState] = useReducer(
        (state, newValues) => ({ ...state, ...newValues }),
        {
            loadingBidButton: false,
            loadingBuyButton: false,
        }
    );
    useEffect(() => {
        setState({
            loadingBidButton: false,
            loadingBuyButton: false,
        });
    }, [data.loadingSummary, data.Sold]);
    const isMobile = useMediaQuery({ query: '(max-width: 600px)' });
    const t = useMemo(() => new Traductor(), []);
    const formEntries = useMemo(() => Object.entries(amountsForm), [
        amountsForm,
    ]);
    const validImport = useCallback(value => {
        return value && +value % 100 === 0;
    }, []);
    const formValues = useMemo(
        () =>
            formEntries.reduce(
                (acc, [key, value]) =>
                    validImport(value)
                        ? {
                              validCars: acc.validCars + 1,
                              total: acc.total + +value,
                          }
                        : acc,
                {
                    validCars: 0,
                    total: 0,
                }
            ),
        [formEntries, validImport]
    );

    const formNumberOfVehicles = formEntries.length;

    const auctionActive = useMemo(
        () =>
            checkAuctionIsActive(
                isAppleDeviceOrNavegatorForDates(data?.StartDate),
                isAppleDeviceOrNavegatorForDates(data?.EndDate),
                serverTime,
                data.upcoming
            ),
        [serverTime, data]
    );

    const disabledBidButton = useMemo(
        () =>
            loading ||
            disabled ||
            formNumberOfVehicles !== formValues.validCars ||
            formValues.total >= directBuyAmount ||
            formValues.total < miniumBidAmount ||
            data.Amount >= formValues.total ||
            (data.Amount &&
                data.Amount + data.DifferentialAmount > formValues.total) ||
            loadingBidButton,
        [
            data.Amount,
            data.DifferentialAmount,
            directBuyAmount,
            disabled,
            formNumberOfVehicles,
            formValues.total,
            formValues.validCars,
            loading,
            loadingBidButton,
            miniumBidAmount,
        ]
    );

    const disabledBuyButton = useMemo(
        () =>
            loading ||
            disabled ||
            formNumberOfVehicles !== formValues.validCars ||
            formValues.total !== directBuyAmount ||
            loadingBuyButton,
        [
            directBuyAmount,
            disabled,
            formNumberOfVehicles,
            formValues.total,
            formValues.validCars,
            loading,
            loadingBuyButton,
        ]
    );

    const renderSpinner = () => {
        return (
            <div
                className="c-spinner c-spinner--small"
                aria-live="polite"
                aria-busy="true"
            >
                <div className="c-spinner__loader">
                    <span className="c-spinner__element"></span>
                    <span className="c-spinner__element"></span>
                    <span className="c-spinner__element"></span>
                    <span className="c-spinner__element"></span>
                    <span className="c-spinner__element"></span>
                    <span className="c-spinner__element"></span>
                    <span className="c-spinner__element"></span>
                    <span className="c-spinner__element"></span>
                </div>
            </div>
        );
    };

    useEffect(() => {
        //Set tooltip content when data is ready, otherwise will be undefined
        if (data) {
            const tooltipDifferentialAmount = document.getElementById(
                'c-closed-batch-tooltip-auction'
            );
            const tooltipDirectBuy = document.getElementById(
                'c-closed-batch-tooltip-direct'
            );
            if (tooltipDifferentialAmount?._tippy) {
                tooltipDifferentialAmount._tippy.setContent(
                    t.getTraduction('DifferentialAmountTooltip')(
                        data.DifferentialAmount
                    )
                );
            }
            if (tooltipDirectBuy?._tippy) {
                tooltipDirectBuy._tippy.setContent(
                    t.getTraduction('DirectPurchaseClosedBatchTooltip')(
                        directBuyAmount
                    )
                );
            }
        }
    }, [data, directBuyAmount, t]);

    const renderRow = useCallback(
        (key, value, keyStyles = '', valueStyles = '') => {
            return (
                <>
                    <dt
                        className={`c-dl-horizontal__title u-inline-item u-mb ${keyStyles}`}
                    >
                        {key}
                    </dt>
                    <dd
                        className={`c-dl-horizontal__item u-text-right u-inline-item u-text-brand u-mb u-font-initial ${valueStyles}`}
                    >
                        {value}
                    </dd>
                </>
            );
        },
        []
    );

    const renderLabel = useCallback(
        (title, tooltip) => {
            return (
                <>
                    <h6
                        className={`c-card__title u-text-left u-pb u-inline-item ${
                            loading ? 'skeleton-details-title u-1/1 u-mb' : ''
                        }`}
                    >
                        {title}
                    </h6>
                    {tooltip && (
                        <button
                            id={tooltip}
                            type="button"
                            className="c-icon c-info-icon js-tooltip c-icon--[semantic-info] c-icon--xxsmall"
                            data-tippy-placement="bottom"
                        />
                    )}
                </>
            );
        },
        [loading]
    );

    return (
        <div
            className={`c-card o-layout__item u-1/4 u-1/1@s ${
                !isMobile
                    ? 'u-inline-item c-sticky-container js-sticky-container u-float-right'
                    : ''
            } u-p-none`}
            style={{
                maxHeight: 'unset',
                ...(disabled
                    ? { cursor: 'default', pointerEvents: 'none', opacity: 0.6 }
                    : {}),
            }}
        >
            <div
                className={`${
                    !isMobile ? 'c-sticky-container__inner' : ''
                } u-ph`}
            >
                <div className="c-card__body" style={{ overflow: 'hidden' }}>
                    <div className="o-layout  o-layout--divider">
                        <div className="o-layout__item  u-1/1">
                            {renderLabel(data?.Name)}
                            <dl className="c-dl-horizontal  u-flex">
                                {renderRow(
                                    t.getTraduction('NumVehiculos'),
                                    data?.NumberOfCars
                                )}
                                {renderRow(
                                    t.getTraduction(
                                        auctionActive
                                            ? 'FinalizaEn'
                                            : 'EmpiezaEn'
                                    ),
                                    <CountDown
                                        dateTime={
                                            auctionActive
                                                ? data?.EndDate
                                                : data?.StartDate
                                        }
                                        isFinishedParent={onCountDownEnd}
                                        serverTime={serverTime}
                                    />
                                )}
                                {renderRow(
                                    t.getTraduction('Import') +
                                        ` [${formValues.validCars}/${formNumberOfVehicles}]`,
                                    `${formatNumber(formValues.total)}€`,
                                    'u-mv u-p-small u-pr-none u-bg-brand u-text-white',
                                    'u-mv u-p-small u-bg-brand u-text-white'
                                )}
                            </dl>
                            <div className="o-layout__item  u-1/1 u-pl-none">
                                <div>
                                    {renderLabel(
                                        t.getTraduction('Auction'),
                                        'c-closed-batch-tooltip-auction'
                                    )}
                                </div>
                                <dl className="c-dl-horizontal  u-flex">
                                    {data.Amount
                                        ? renderRow(
                                              t.getTraduction('CurrentBid'),
                                              `${formatNumber(data.Amount)}€`,
                                              '',
                                              'u-text-bold'
                                          )
                                        : renderRow(
                                              t.getTraduction('StartingPrice'),
                                              `${formatNumber(
                                                  miniumBidAmount
                                              )}€`,
                                              '',
                                              'u-text-bold'
                                          )}
                                </dl>
                                <button
                                    className="c-btn c-btn--full u-mb"
                                    disabled={disabledBidButton}
                                    onClick={() => {
                                        setState({ loadingBidButton: true });
                                        onBid(formEntries, formValues.total);
                                    }}
                                >
                                    <span className="c-btn__text">
                                        {!loading && t.getTraduction('BidUp')}
                                    </span>
                                    {loadingBidButton && renderSpinner()}
                                </button>
                            </div>
                        </div>
                        <div className="o-layout__item  u-1/1">
                            <div>
                                {renderLabel(
                                    t.getTraduction('CompraDirecta'),
                                    'c-closed-batch-tooltip-direct'
                                )}
                            </div>
                            <dl className="c-dl-horizontal  u-flex">
                                {renderRow(
                                    t.getTraduction('DirectPurchasePrice'),
                                    `${formatNumber(directBuyAmount)}€`
                                )}
                            </dl>
                            <button
                                className="c-btn  c-btn--full"
                                disabled={disabledBuyButton}
                                onClick={() => {
                                    setState({ loadingBuyButton: true });
                                    onDirectBuy();
                                }}
                            >
                                <span className="c-btn__text">
                                    {!loading && t.getTraduction('BuyBatch')}
                                </span>
                                {loadingBuyButton && renderSpinner()}
                            </button>
                        </div>
                    </div>
                    <p
                        className="u-mt"
                        style={{
                            color: 'gray',
                            textJustify: 'auto',
                            textAlign: 'initial',
                        }}
                    >
                        {t.getTraduction('BatchTransportReminder')}
                    </p>
                </div>
            </div>
        </div>
    );
};

Summary.propTypes = {
    data: PropTypes.shape({
        Name: PropTypes.string,
        DifferentialAmount: PropTypes.number,
        Amount: PropTypes.number,
        AmountMax: PropTypes.number,
        StartDate: PropTypes.string,
        EndDate: PropTypes.string,
        NumberOfCars: PropTypes.number,
    }),
    directBuyAmount: PropTypes.number,
    miniumBidAmount: PropTypes.number,
    loading: PropTypes.bool,
    amountsForm: PropTypes.object,
    onBid: PropTypes.func.isRequired,
    onDirectBuy: PropTypes.func.isRequired,
    disabled: PropTypes.any,
    onCountDownEnd: PropTypes.func.isRequired,
    serverTime: PropTypes.string,
};

export default memo(Summary);
